import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL, fetchFile } from "@ffmpeg/util";
import { useCallback, useRef, useState } from "react";
import * as tf from "@tensorflow/tfjs";

import { useAppContext } from "../hooks/useAppContext";
// 1. Import your Autonomous Kernel
import { useKernel } from "../hooks/AIKernel"; 
import logo from "./Preview.jpg";

const BASE_URL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";

export default function useEditVideoFile() {
  const { videoRef, setIsProcessing } = useAppContext();
  // 2. Tap into the Agent's Nervous System
  const { agentState, reportBehavior } = useKernel(); 
  
  const [progress, setProgress] = useState(0);
  const ffmpegRef = useRef(new FFmpeg());

  const loadFFmpeg = async () => {
    const ffmpeg = ffmpegRef.current;
    if (ffmpeg.loaded) return;

    ffmpeg.on("log", ({ message }) => console.log("[FFmpeg Log]:", message));
    ffmpeg.on("progress", ({ progress }) => setProgress(Math.round(progress * 100)));

    try {
      await ffmpeg.load({
        coreURL: await toBlobURL(`${BASE_URL}/ffmpeg-core.js`, "text/javascript"),
        wasmURL: await toBlobURL(`${BASE_URL}/ffmpeg-core.wasm`, "application/wasm"),
      });
      console.log("FFmpeg loaded successfully.");
      
      // Telemetry: Note that the user has initialized the heavy editor
      if (reportBehavior) reportBehavior("video_editor_active", 2, "behavior");
    } catch (error) {
      console.error("Error loading FFmpeg:", error);
    }
  };

  // --- CORE VIDEO EDITING ---

  const cutVideo = useCallback(async (operations: Array<[number, number]>) => {
    const ffmpeg = ffmpegRef.current;
    if (!videoRef?.src) return false;

    setIsProcessing(true);
    await loadFFmpeg();
    
    // Telemetry: Record manual editing interest
    if (reportBehavior) reportBehavior("manual_video_trim", 5, "interest");

    // Adaptive Engine: If frustrated, speed up the render at the cost of compression efficiency
    const isFrustrated = agentState?.ui?.simplifyAnimations;
    const renderPreset = isFrustrated ? "ultrafast" : "fast";

    try {
      const videoBlob = await (await fetch(videoRef.src)).blob();
      await ffmpeg.writeFile("input.mp4", await fetchFile(videoBlob));

      const segmentFiles = await Promise.all(
        operations.map(async ([start, end], index) => {
          const outputSegment = `segment_${index}.mp4`;
          await ffmpeg.exec([
            "-i", "input.mp4", 
            "-ss", start.toString(), 
            "-t", (end - start).toString(), 
            "-c:v", "libx264", 
            "-preset", renderPreset, // Dynamically applied preset
            "-c:a", "copy", 
            outputSegment
          ]);
          return outputSegment;
        })
      );

      const fileListContent = segmentFiles.map(file => `file '${file}'`).join("\n");
      await ffmpeg.writeFile("fileList.txt", new TextEncoder().encode(fileListContent));
      
      await ffmpeg.exec(["-f", "concat", "-safe", "0", "-i", "fileList.txt", "-c", "copy", "output.mp4"]);

      const data = await ffmpeg.readFile("output.mp4");
      setIsProcessing(false);
      return URL.createObjectURL(new Blob([data as any], { type: "video/mp4" }));
    } catch (error) {
      console.error("Error cutting video:", error);
      setIsProcessing(false);
      return false;
    }
  }, [setIsProcessing, videoRef, agentState, reportBehavior]);


  // --- ADVANCED AUDIO & MEDIA FEATURES ---

  const extractAudio = useCallback(async () => {
    const ffmpeg = ffmpegRef.current;
    if (!videoRef?.src) return false;

    setIsProcessing(true);
    await loadFFmpeg();
    if (reportBehavior) reportBehavior("audio_extraction", 8, "interest");

    try {
      await ffmpeg.writeFile("input.mp4", await fetchFile(await (await fetch(videoRef.src)).blob()));
      await ffmpeg.exec(["-i", "input.mp4", "-q:a", "0", "-map", "a", "output.mp3"]);

      const data = await ffmpeg.readFile("output.mp3");
      setIsProcessing(false);
      return URL.createObjectURL(new Blob([data as any], { type: "audio/mp3" }));
    } catch (error) {
      console.error("Error extracting audio:", error);
      setIsProcessing(false);
      return false;
    }
  }, [setIsProcessing, videoRef, reportBehavior]);

  const generateGif = useCallback(async (startTime: number, duration: number) => {
    const ffmpeg = ffmpegRef.current;
    if (!videoRef?.src) return false;

    setIsProcessing(true);
    await loadFFmpeg();
    if (reportBehavior) reportBehavior("gif_generation", 5, "interest");

    try {
      await ffmpeg.writeFile("input.mp4", await fetchFile(await (await fetch(videoRef.src)).blob()));
      await ffmpeg.exec([
        "-ss", startTime.toString(), 
        "-t", duration.toString(), 
        "-i", "input.mp4", 
        "-vf", "fps=10,scale=320:-1:flags=lanczos,split[s0][s1];[s0]palettegen[p];[s1][p]paletteuse", 
        "-loop", "0", 
        "output.gif"
      ]);

      const data = await ffmpeg.readFile("output.gif");
      setIsProcessing(false);
      return URL.createObjectURL(new Blob([data as any], { type: "image/gif" }));
    } catch (error) {
      console.error("Error generating GIF:", error);
      setIsProcessing(false);
      return false;
    }
  }, [setIsProcessing, videoRef, reportBehavior]);

  const addBackgroundAudio = useCallback(async (audioSrc: string) => {
    const ffmpeg = ffmpegRef.current;
    if (!videoRef?.src) return false;

    setIsProcessing(true);
    await loadFFmpeg();
    if (reportBehavior) reportBehavior("audio_mixing", 6, "interest");

    try {
      await ffmpeg.writeFile("input.mp4", await fetchFile(await (await fetch(videoRef.src)).blob()));
      await ffmpeg.writeFile("bg_music.mp3", await fetchFile(await (await fetch(audioSrc)).blob()));

      await ffmpeg.exec([
        "-i", "input.mp4", 
        "-stream_loop", "-1", "-i", "bg_music.mp3", 
        "-c:v", "copy", 
        "-c:a", "aac", 
        "-map", "0:v:0", "-map", "1:a:0", 
        "-shortest", 
        "output.mp4"
      ]);

      const data = await ffmpeg.readFile("output.mp4");
      setIsProcessing(false);
      return URL.createObjectURL(new Blob([data as any], { type: "video/mp4" }));
    } catch (error) {
      console.error("Error adding background audio:", error);
      setIsProcessing(false);
      return false;
    }
  }, [setIsProcessing, videoRef, reportBehavior]);


  // --- TENSORFLOW AI SCENE DETECTION ---

  const loadModel = async () => {
    const modelUrl = "https://tfhub.dev/google/mobilenet_v2_130_224/classification/3/model.json";
    return await tf.loadLayersModel(modelUrl);
  };

  // Adaptive Engine: Accepts dynamic sampling intervals
  const extractFrames = async (video: HTMLVideoElement, model: tf.LayersModel, sampleIntervalMs: number) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas rendering context is not available.");

    const frames: tf.Tensor[] = [];

    for (let time = 0; time < video.duration; time += sampleIntervalMs / 1000) {
      video.currentTime = time;
      await new Promise((resolve) => (video.onseeked = resolve));

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const prediction = tf.tidy(() => {
        const imageTensor = tf.browser.fromPixels(canvas).resizeBilinear([224, 224]).expandDims(0).toFloat();
        return model.predict(imageTensor) as tf.Tensor;
      });
      
      frames.push(tf.keep(prediction)); 
    }
    return frames;
  };

  const detectSceneChanges = (frames: tf.Tensor[]) => {
    const sceneCuts: number[] = [];
    let lastFrame = frames[0];

    for (let i = 1; i < frames.length; i++) {
      const diffVal = tf.tidy(() => {
        const diff = tf.sub(frames[i], lastFrame).abs().mean();
        return diff.dataSync()[0];
      });

      if (diffVal > 0.2) sceneCuts.push(i);
      lastFrame = frames[i];
    }

    frames.forEach(frame => frame.dispose());
    return sceneCuts;
  };

  const trimVideoAI = async (ffmpeg: FFmpeg, sceneCuts: number[], sampleIntervalMs: number, renderPreset: string) => {
    if (!sceneCuts.length) return false;

    let startTime = 0;
    const segmentFiles: string[] = [];

    for (let index = 0; index < sceneCuts.length; index++) {
      const endTime = sceneCuts[index] * (sampleIntervalMs / 1000); 
      const outputName = `ai_part_${index}.mp4`;
      
      await ffmpeg.exec([
        "-i", "input.mp4",
        "-ss", startTime.toString(),
        "-to", endTime.toString(),
        "-c:v", "libx264", "-preset", renderPreset,
        outputName
      ]);
      
      segmentFiles.push(outputName);
      startTime = endTime;
    }

    const fileListContent = segmentFiles.map(file => `file '${file}'`).join("\n");
    await ffmpeg.writeFile("fileList.txt", new TextEncoder().encode(fileListContent));
    await ffmpeg.exec(["-f", "concat", "-safe", "0", "-i", "fileList.txt", "-c", "copy", "ai_trimmed.mp4"]);

    return "ai_trimmed.mp4";
  };

  const AIprocessVideoFullCorrection = useCallback(async () => {
    const ffmpeg = ffmpegRef.current;
    if (!videoRef?.src) return false;

    setIsProcessing(true);
    await loadFFmpeg();
    
    // Telemetry: Heavy AI processing signals high interest in machine learning tools
    if (reportBehavior) reportBehavior("ai_video_automation", 15, "interest");

    // Adaptive Engine: Read frustration levels to dictate AI processing speed
    const isFrustrated = agentState?.ui?.simplifyAnimations;
    const renderPreset = isFrustrated ? "ultrafast" : "fast";
    const aiSamplingInterval = isFrustrated ? 1000 : 500; // If frustrated, sample 1 frame per sec instead of 2

    try {
      const videoBlob = await (await fetch(videoRef.src)).blob();
      await ffmpeg.writeFile("input.mp4", await fetchFile(videoBlob));

      // 1. Scene Detection & AI Trim (Using Adaptive Variables)
      const model = await loadModel();
      const frameData = await extractFrames(videoRef, model, aiSamplingInterval);
      const sceneCuts = detectSceneChanges(frameData);
      
      if (sceneCuts.length > 0) {
        await trimVideoAI(ffmpeg, sceneCuts, aiSamplingInterval, renderPreset);
        await ffmpeg.exec(["-i", "ai_trimmed.mp4", "-c", "copy", "pipeline_1.mp4"]); 
      } else {
        await ffmpeg.exec(["-i", "input.mp4", "-c", "copy", "pipeline_1.mp4"]);
      }

      // 2. Color Correction (Adaptive Preset)
      await ffmpeg.exec([
        "-i", "pipeline_1.mp4",
        "-vf", "eq=brightness=0.05:saturation=1.2",
        "-c:v", "libx264", "-preset", renderPreset,
        "pipeline_2.mp4"
      ]);

      // 3. Watermark Overlay (Adaptive Preset)
      const logoBlob = await (await fetch(logo)).blob();
      await ffmpeg.writeFile("logo.jpg", await fetchFile(logoBlob));

      await ffmpeg.exec([
        "-i", "pipeline_2.mp4",
        "-i", "logo.jpg", 
        "-filter_complex", "overlay=10:10",
        "-c:v", "libx264", "-preset", renderPreset,
        "final_output.mp4"
      ]);

      const data = await ffmpeg.readFile("final_output.mp4");
      setIsProcessing(false);
      return URL.createObjectURL(new Blob([data as any], { type: "video/mp4" }));
    } catch (error) {
      console.error("Error processing AI video:", error);
      setIsProcessing(false);
      return false;
    }
  }, [setIsProcessing, videoRef, agentState, reportBehavior]);

  const overlayImageOnVideo = useCallback(async (image: string) => {
    const ffmpeg = ffmpegRef.current;
    if (!videoRef?.src) return false;

    setIsProcessing(true);
    await loadFFmpeg();
    if (reportBehavior) reportBehavior("branding_overlay", 4, "interest");

    const renderPreset = agentState?.ui?.simplifyAnimations ? "ultrafast" : "fast";

    try {
      const videoBlob = await (await fetch(videoRef.src)).blob();
      const imageBlob = await (await fetch(image)).blob();

      await ffmpeg.writeFile("input.mp4", await fetchFile(videoBlob));
      await ffmpeg.writeFile("logo.png", await fetchFile(imageBlob));

      await ffmpeg.exec([
        "-i", "input.mp4", 
        "-i", "logo.png", 
        "-filter_complex", "overlay", 
        "-preset", renderPreset, 
        "-c:a", "copy", 
        "output.mp4"
      ]);

      const data = await ffmpeg.readFile("output.mp4");
      setIsProcessing(false);
      return URL.createObjectURL(new Blob([data as any], { type: "video/mp4" }));
    } catch (error) {
      console.error("Error overlaying image:", error);
      setIsProcessing(false);
      return false;
    }
  }, [setIsProcessing, videoRef, agentState, reportBehavior]);

  const transcodeVideo = useCallback(async (video: string) => {
    const ffmpeg = ffmpegRef.current;
    setIsProcessing(true);

    if (!ffmpeg.loaded) await loadFFmpeg();

    const renderPreset = agentState?.ui?.simplifyAnimations ? "ultrafast" : "medium";

    try {
      const videoBlob = await (await fetch(video)).blob();
      await ffmpeg.writeFile("input.mp4", await fetchFile(videoBlob));

      await ffmpeg.exec([
        "-i", "input.mp4",
        "-c:v", "libx264",
        "-profile:v", "main",
        "-vf", "format=yuv420p",
        "-c:a", "aac",
        "-movflags", "+faststart",
        "-preset", renderPreset,
        "output.mp4"
      ]);

      const data = await ffmpeg.readFile("output.mp4");
      setIsProcessing(false);
      return URL.createObjectURL(new Blob([data as any], { type: "video/mp4" }));
    } catch (error) {
      console.error("Error transcoding video:", error);
      setIsProcessing(false);
      return false;
    }
  }, [setIsProcessing, agentState]);

  return { 
    cutVideo, 
    progress, 
    AIprocessVideoFullCorrection,
    extractAudio,
    generateGif,
    addBackgroundAudio,
    overlayImageOnVideo,
    transcodeVideo
  };
}